import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { IOSGuide } from "@/components/install/IOSGuide";
import { ChromeGuide, NoPromptGuide, chromeIntent } from "@/components/install/AndroidGuides";
import { Primary, Sheet, Later } from "@/components/install/ui";

const KEY = "faunny_install_dismissed_at";
const DAYS14 = 14 * 24 * 60 * 60 * 1000;
const UA = navigator.userAgent;
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const IOS = /iphone|ipad|ipod/i.test(UA) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const ANDROID = /android/i.test(UA);
const ALT_ANDROID = ANDROID && /SamsungBrowser|; wv\)|FBAN|FBAV|Instagram|WhatsApp|MiuiBrowser|HuaweiBrowser|HeyTapBrowser|OPR\/|YaBrowser/i.test(UA);
const dismissedRecently = () => Date.now() - (+localStorage.getItem(KEY) || 0) < DAYS14;

const takeInstallFlag = () => {
  const u = new URL(window.location.href);
  if (!u.searchParams.has("instalar")) return false;
  u.searchParams.delete("instalar");
  window.history.replaceState(null, "", u.pathname + u.search + u.hash);
  return true;
};

const openChrome = (fallback) => {
  window.location.href = chromeIntent();
  setTimeout(() => { if (document.visibilityState === "visible") fallback(); }, 1800);
};

export const InstallBanner = () => {
  const [prompt, setPrompt] = useState(() => window.__installPrompt || null);
  const [arrived] = useState(takeInstallFlag);
  const [hidden, setHidden] = useState(() => isStandalone() || (!arrived && dismissedRecently()));
  const [guide, setGuide] = useState(null);

  useEffect(() => {
    const ready = () => setPrompt(window.__installPrompt);
    const installed = () => { setHidden(true); setGuide(null); };
    window.addEventListener("faunny-install-ready", ready);
    window.addEventListener("appinstalled", installed);
    return () => { window.removeEventListener("faunny-install-ready", ready); window.removeEventListener("appinstalled", installed); };
  }, []);

  useEffect(() => {
    if (!arrived || isStandalone()) return;
    if (prompt) { setGuide("oneTap"); return; }
    const t = setTimeout(() => setGuide((g) => g || "menu"), 4000);
    return () => clearTimeout(t);
  }, [arrived, prompt]);

  const runPrompt = async () => {
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__installPrompt = null;
    setPrompt(null);
    setGuide(null);
    if (outcome === "accepted") setHidden(true);
  };

  const install = () => {
    if (IOS) return setGuide("ios");
    if (ALT_ANDROID) return openChrome(() => setGuide("chrome"));
    if (prompt) return runPrompt();
    setGuide("menu");
  };

  const close = () => { localStorage.setItem(KEY, String(Date.now())); setHidden(true); };
  const closeGuide = () => setGuide(null);
  const showBar = !hidden && (prompt || IOS || ALT_ANDROID);

  return (
    <>
      {showBar && (
        <div className="bg-white border-b-4 border-[#3D88B8] animate-in fade-in slide-in-from-top-2 duration-300" data-testid="install-banner">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
            <img src="/icons/icon-192.png" alt="" className="w-10 h-10 rounded-lg border border-slate-200 shrink-0" />
            <p className="flex-1 text-sm sm:text-base text-[#0B1D3A] leading-snug"><b className="text-[#B23A38]">Lleva Faunny siempre contigo.</b> Instala la app gratis</p>
            <button onClick={install} className="inline-flex items-center gap-1.5 bg-[#B23A38] hover:bg-[#962F2D] active:scale-95 text-white font-bold px-4 py-2 rounded-full transition-[background-color,transform] shrink-0" data-testid="install-button"><Download size={16} />Instalar</button>
            <button onClick={close} aria-label="Cerrar" className="p-1.5 text-slate-500 hover:text-[#3D88B8] transition-colors shrink-0" data-testid="install-close"><X size={20} /></button>
          </div>
        </div>
      )}
      {guide === "ios" && <IOSGuide onClose={closeGuide} />}
      {guide === "chrome" && <ChromeGuide onClose={closeGuide} />}
      {guide === "menu" && <NoPromptGuide onClose={closeGuide} />}
      {guide === "oneTap" && prompt && (
        <Sheet title="¡Ya casi está!" onClose={closeGuide} testId="install-onetap">
          <p className="mt-3 text-lg text-slate-700">Toca el botón y Faunny quedará en tu pantalla de inicio.</p>
          <Primary onClick={runPrompt} data-testid="install-onetap-button"><Download size={24} />Instalar Faunny</Primary>
          <Later onClick={closeGuide} data-testid="install-onetap-later" />
        </Sheet>
      )}
    </>
  );
};
